import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { earningsProgress } from "@/lib/earningsProgress";
import { formatting } from "@/lib/formatting";
import { teamGoals } from "@/lib/teamGoals";
import { cn } from "@/lib/utils";
import { EarningsCommissionTier, EarningsTierSegmentState } from "@/types";

const TRACK_COLOR = "#E8EAED";
const PASSED_TIER_OPACITY = 0.28;
// Hex alpha suffixes appended to the segment color (B3 ≈ 70%, 59 ≈ 35%)
const LABEL_BORDER_ALPHA = "B3";
const MUTED_FILL_ALPHA = "59";

// Segmented tier progress bar: one bar per commission tier, filled by YTD revenue
export const EarningsProgressBar = ({
  tiers,
  totalRevenueDollars,
}: {
  tiers: EarningsCommissionTier[];
  totalRevenueDollars: number;
}) => {
  const currentTierIndex = earningsProgress.getCurrentTierIndex(
    totalRevenueDollars,
    tiers,
  );
  const nextTierProgress = earningsProgress.getDollarsToNextTier(
    totalRevenueDollars,
    tiers,
  );

  /* Helpers */
  const getNextTierTooltipLabel = () => {
    if (!nextTierProgress) {
      return null;
    }

    return `${formatting.currency(nextTierProgress.dollarsRemaining)} to reach ${nextTierProgress.nextCommissionPercent.toString()}%`;
  };

  const getFirstTierRevenueCapLabel = () => {
    if (!tiers[1]?.thresholdDollars) {
      return null;
    }

    return `Up to ${teamGoals.formatRevenueGoal(tiers[1].thresholdDollars)}`;
  };

  /* Render */
  const render = () => (
    <TooltipProvider delayDuration={150}>
      <Row className="min-h-0 w-full min-w-0 items-stretch gap-x-1.5">
        {tiers.map((tier, index) => {
          const color = earningsProgress.tickColorAtSegmentIndex(
            index,
            tiers.length,
          );
          const { fillRatio, state } = earningsProgress.getTierSegmentState(
            totalRevenueDollars,
            tiers,
            index,
          );
          const isCurrentTier = index === currentTierIndex;
          const isPassedTier =
            currentTierIndex >= 1 &&
            index < currentTierIndex &&
            state === EarningsTierSegmentState.COMPLETE;

          return (
            <Column
              key={tier.commissionPercent}
              className="min-w-0 gap-4"
              style={{
                flexBasis: 0,
                flexGrow: earningsProgress.getSegmentFlexGrow(
                  tiers.length,
                  index,
                ),
              }}
            >
              <SegmentBar
                color={color}
                fillRatio={fillRatio}
                isPassedTier={isPassedTier}
                state={state}
                tooltipLabel={isCurrentTier ? getNextTierTooltipLabel() : null}
              />
              <TierLabel
                color={color}
                isFirstTier={index === 0}
                revenueCapLabel={getFirstTierRevenueCapLabel()}
                tier={tier}
              />
            </Column>
          );
        })}
      </Row>
    </TooltipProvider>
  );

  return render();
};

// Commission % + threshold under a segment; the first tier also gets an explainer tooltip
const TierLabel = ({
  color,
  isFirstTier,
  revenueCapLabel,
  tier,
}: {
  color: string;
  isFirstTier: boolean;
  revenueCapLabel: string | null;
  tier: EarningsCommissionTier;
}) => {
  const thresholdLabel = isFirstTier
    ? revenueCapLabel
    : formatting.currency(tier.thresholdDollars);

  /* Render */
  const renderLabelColumn = () => (
    <Column
      className="min-w-0 items-start gap-1 border-l-[3px] pl-2"
      style={{ borderColor: `${color}${LABEL_BORDER_ALPHA}` }}
    >
      <Text className="text-left text-xl font-semibold tabular-nums leading-none text-slate-900/80">
        {tier.commissionPercent.toString()}%
      </Text>
      {thresholdLabel ? (
        <Text className="whitespace-nowrap text-left text-xs font-medium leading-tight text-slate-500">
          {thresholdLabel}
        </Text>
      ) : null}
    </Column>
  );

  const render = () => {
    if (!isFirstTier) {
      return renderLabelColumn();
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-default">{renderLabelColumn()}</div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <Column className="gap-0.5">
            <Text className="text-xs font-medium">Commission Rate</Text>
            {revenueCapLabel ? (
              <Text className="text-xs font-medium">
                {`${revenueCapLabel} • Total Revenue`}
              </Text>
            ) : null}
          </Column>
        </TooltipContent>
      </Tooltip>
    );
  };

  return render();
};

// Fill inside a segment's track, driven by the tier's state
const SegmentFill = ({
  color,
  fillRatio,
  isPassedTier,
  showHoverPreview,
  state,
}: {
  color: string;
  fillRatio: number;
  isPassedTier: boolean;
  showHoverPreview: boolean;
  state: EarningsTierSegmentState;
}) => {
  /* Render */
  const render = () => {
    if (state === EarningsTierSegmentState.COMPLETE) {
      return (
        <div
          className="h-full w-full rounded-full"
          style={
            isPassedTier
              ? { backgroundColor: color, opacity: PASSED_TIER_OPACITY }
              : { backgroundColor: color }
          }
        />
      );
    }

    if (state === EarningsTierSegmentState.UPCOMING) {
      return (
        <div
          className="h-full w-full rounded-full"
          style={{ backgroundColor: `${color}${MUTED_FILL_ALPHA}` }}
        />
      );
    }

    // Current tier
    return (
      <>
        <div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            width: `${(fillRatio * 100).toString()}%`,
            backgroundColor: color,
          }}
        />
        {showHoverPreview ? (
          // Hover preview of the rest of the current tier
          <div
            className="absolute inset-y-0 rounded-full opacity-0 transition-opacity group-hover:opacity-100"
            style={{
              left: `${(fillRatio * 100).toString()}%`,
              right: 0,
              backgroundColor: `${color}${MUTED_FILL_ALPHA}`,
            }}
          />
        ) : null}
      </>
    );
  };

  return render();
};

// One tier's track + fill; wraps in a tooltip when there's a next tier to preview
const SegmentBar = ({
  color,
  fillRatio,
  isPassedTier,
  state,
  tooltipLabel,
}: {
  color: string;
  fillRatio: number;
  isPassedTier: boolean;
  state: EarningsTierSegmentState;
  tooltipLabel: string | null;
}) => {
  /* Render */
  const renderBar = () => (
    <div
      className={cn(
        "group relative h-4 w-full min-w-0 overflow-hidden rounded-full shadow-sm ring-1 ring-slate-900/10",
        isPassedTier && "ring-slate-200/80",
        tooltipLabel && "cursor-default",
      )}
      style={{ backgroundColor: TRACK_COLOR }}
    >
      <SegmentFill
        color={color}
        fillRatio={fillRatio}
        isPassedTier={isPassedTier}
        showHoverPreview={!!tooltipLabel}
        state={state}
      />
    </div>
  );

  const render = () => {
    if (!tooltipLabel) {
      return renderBar();
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{renderBar()}</TooltipTrigger>
        <TooltipContent side="top">{tooltipLabel}</TooltipContent>
      </Tooltip>
    );
  };

  return render();
};
